import type { AssetMediaRef } from "./index";

type JsonRecord = Record<string, unknown>;

/**
 * The string fields of `AssetMediaRef` besides `id` and `kind`: the one list
 * every reader of a media ref's shape derives from (the operations parser, the
 * accepted-artifact contract). A writer that stores any other field on a media
 * ref writes outside the contract (QA4 A2 #7: a URL capture's `sourceUrl` and
 * `metadata` made every later Save of that page a 400).
 */
export const ASSET_MEDIA_REF_STRING_FIELDS = [
  "fileId",
  "filePath",
  "bucket",
  "objectKey",
  "gcsUri",
  "publicUrl",
  "mimeType",
  "url",
  "altText",
  "label",
  "sliceOf",
  "sliceRole",
] as const;

type MediaStringField = (typeof ASSET_MEDIA_REF_STRING_FIELDS)[number];
type UnlistedMediaField = Exclude<
  keyof AssetMediaRef,
  MediaStringField | "id" | "kind" | "sliceIndex"
>;
const _MEDIA_FIELDS_EXHAUSTIVE: [UnlistedMediaField] extends [never]
  ? true
  : "ASSET_MEDIA_REF_STRING_FIELDS must list every string field of AssetMediaRef" =
  true;
void _MEDIA_FIELDS_EXHAUSTIVE;

const isRecord = (value: unknown): value is JsonRecord =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);
const nonBlank = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

/**
 * Whether a non-image media record names bytes or a URL that downstream
 * delivery can resolve. Images have the stricter GCS-only predicate owned by
 * the image ingestion path; this predicate owns video/document/url-snapshot
 * locators shared by intake, explicit asset updates, and accepted state.
 */
export function hasUsableNonImageAssetMediaLocator(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    nonBlank(value.fileId) ||
    nonBlank(value.filePath) ||
    nonBlank(value.url) ||
    nonBlank(value.publicUrl) ||
    nonBlank(value.gcsUri) ||
    (nonBlank(value.bucket) && nonBlank(value.objectKey))
  );
}
